import { useEffect, useState } from 'react'
import { AudioPlayerBar } from '@/components/AudioPlayerBar'
import { GeneratorFrame, PriceConfirmDialog, StudioPanel } from '@/components/studio'
import { Button, Drawer, Segmented } from '@/components/ui'
import { Icon } from '@/components/icons'
import { VoiceLibrary } from '@/components/VoiceLibrary'
import { estimateAudioMs, modelLabel } from '@/config/catalog'
import { DEFAULT_FAMILY_ID } from '@/config/modelFamilies'
import { DEFAULT_VOICE_ID, pickVoiceId } from '@/config/voices'
import { useJobCenter } from '@/context/JobCenterContext'
import { useModels } from '@/context/ModelsContext'
import { useDraft } from '@/hooks/useDraft'
import { useGenerateFlow } from '@/hooks/useGenerateFlow'
import { usePricePreview } from '@/hooks/usePricePreview'
import { useVoices } from '@/hooks/useVoices'
import type { JobOutput } from '@/api/types'

/**
 * 语音合成工作台（`/tts`）—— 口播稿 → mp3。
 *
 * ## 它为什么值得一个独立页面
 *
 * 它落在侧栏「② 文案」组下，因为**输入物就是一份文案**（口播稿），
 * 只是产出形态不是字而是声音。与「文案工作台」不合并：那一页的出稿是**给人看**的
 * （要拷进后台、要改措辞、要按平台分渠道），这一页的产出是**给机器读**的
 * （喂给数字人、喂给剪辑、直播时当音轨）。同一份稿子在两处的心智完全不同，
 * 合并只会让两侧的页签都变成大杂烩。
 *
 * ## 🔴 它是全站唯一「一屏到底」的生成页（2026-10-08 用户拍板）
 *
 * 用户指定照 `minimax.cn` 的语音合成页「整体模仿」，并在我明确提示
 * 「扁平档在本项目被否过两次」之后，**仍然选了「彻底摊平成一屏」**。
 * 所以这是一次**有记录的架构例外**，不是顺手摊平：
 *
 *   · 其余 9 个生成页仍然是 `WorkflowTabs` 2-4 页签（`smoke-nav.ts` 里有断言守着）；
 *   · 本页不再挂 `WorkflowTabs`，配套改动见该文件里那段
 *     「**除 /tts 外**全站必须分页签」+ 定向断言本页结构的检查；
 *   · `step_tts` 这个 localStorage 键随之作废 —— 页签没了就没有「第几步」。
 *     刻意**不写迁移**：它只是个整数下标，留着不读不会被任何人看见。
 *
 * ⚠️ **别把这套版面复制到别的页面**。它能成立的前提是本页的字段极少
 * （一段稿子 + 一个音色 + 一档语速），一屏放得下且不需要滚。
 * 换到套图/视频页（字段 20+）就会退回到 2026-09-28 被驳回的那个版本 ——
 * 用户当时的原话是「现在左边的 form 太长了，操作不友好」。
 *
 * ## 三条最容易做错的
 *
 * ① **音色清单必须来自接口**（`GET /api/aigc/voices`，见 `useVoices`），
 *    页面上一个音色 ID 都不许硬编码。硬编码一份的话，上游删掉一条、
 *    页面照样把它发出去 —— 症状是**选了 A 出来 B**，而任务状态全绿
 *    （上游对不认识的音色 ID 是**悄悄换默认音色**，不是报错）。
 *
 * ② **界面显示的音色 = 提交载荷里的音色 = 后端实际合成的音色**。
 *    所以提交读的是 `voiceId`（落过清单校验的派生值），不是草稿里的 `form.voice`
 *    —— 草稿可能是换引擎前存的，老 ID 在当前清单里不存在，原样发出去就是 ①。
 *
 * ③ **没有「出几条」这个控件，是刻意的**。后端一次任务 = 一段稿子 = 一条 mp3；
 *    长稿由 `synthesize_voiceover` 按 skill 的 chunk 上限切段、合成完再拼接
 *    （实测 945 字 → 7.4 秒出片），用户看到的仍是**一条**。
 *    摆一个「条数」控件而载荷不按它产出，就是「界面说的和实际发的不一致」——
 *    本项目最难查的那一类。
 *
 * ## 报价与参数的一致性
 *
 * `outputs` 恒为 1，报价按 1 条算、载荷按 1 条发。后端 `_run_audio_phase` 有一条
 * 硬校验「报价条数必须等于文本段数」，两处不一致会当场报错而不是静默少合一条。
 */

type FormState = {
  /** 口播稿。**这就是会被念出来的全文** —— 提交时原样进 `prompt` */
  text: string
  /** 上游音色 ID（不是中文展示名），与草稿、载荷、后端三处同一个值 */
  voice: string
  /** 语速。1 = 上游默认；界面只给几档，不让随便填（异常值上游不回错、只是不生效） */
  speed: number
  /** 模型族 id。语音合成只有一档，但仍然走真源，不许写死在提交处 */
  model: string
}

const DEFAULTS: FormState = {
  text: '',
  // 与后端 `ENGINE_DEFAULTS[ENGINE_DEFAULT]` 逐字一致，由后端测试跨仓库钉住
  voice: DEFAULT_VOICE_ID,
  speed: 1,
  model: DEFAULT_FAMILY_ID.audio,
}

/**
 * 语速档位。
 *
 * 为什么是**固定几档**而不是自由输入：上游对语速的容忍度是 0.5–2，但
 * **传异常值不报错**（回一个「成功但语速没变」的响应）。用户手填 3.5 之后
 * 听到的还是 1.0 倍速，而界面上写着 3.5 —— 没有任何地方能看出病因。
 * 砍成四档，用户能选到的每一个都真的会生效。
 */
const SPEEDS: { value: string; label: string }[] = [
  { value: '0.8', label: '慢 0.8×' },
  { value: '1', label: '标准 1.0×' },
  { value: '1.2', label: '快 1.2×' },
  { value: '1.5', label: '很快 1.5×' },
]

/**
 * 预估耗时（毫秒）—— **公式的真源在 `config/catalog.ts` 的 `estimateAudioMs`**。
 *
 * ⚠️ 2026-10-09 从这里搬走：数字人页的「生成配音」提交的是**同一档音频任务**，
 * 两处各留一份公式的话，改了一边另一边静默漂开（症状只是「那个页面进度条不准」）。
 * 后端另有一条兜底（`_default_estimate('audio')` = 20 秒），只在页面没传时生效。
 */
const estimateMs = estimateAudioMs

/** 页签 key。**与 `jobsFor` / `sourcePage` 是同一个字符串**，三处必须一起改。 */
const PAGE_KEY = 'tts'

/**
 * 长稿阈值。
 *
 * ⚠️ 它只用来决定**要不要多说一句「会自动切段」**，不是提交参数 ——
 * 真正的切段由 skill 自己按上游上限做，前端写死一个数字去卡提交
 * 只会在上游调上限时变成「界面拦着不让提交，而后端本来能跑」。
 */
const LONG_SCRIPT_CHARS = 800

export function TtsStudio() {
  const { value: form, patch, reset, savedAt } = useDraft(PAGE_KEY, DEFAULTS)
  const { cancel, jobsFor, outputsFor, clearResults } = useJobCenter()
  const flow = useGenerateFlow()
  const voices = useVoices()
  /** 音色库抽屉。音色是这一个页面里唯一「有 300 个候选」的字段，值得一层浮层 */
  const [voiceOpen, setVoiceOpen] = useState(false)

  /**
   * 底部试听条当前装的是哪一条 —— **整份描述**（地址 + 标题 + 副标题），
   * 不是一个可以被反复回查的地址。
   *
   * 🔴 2026-10-09 报障「历史里的播放键点了没反应 / 播出来的不是那条」。
   * 旧写法只存 `{url}`，渲染时再拿它去**本轮产出**里现取；而历史记录里的产出
   * 不属于本轮产出（`outputsFor` 刻意排除远端记录）—— 于是：
   *   · 本轮有产出 → `find` 落空 → **回落到最新一条**，点了 A 响的是 B，
   *     条上标题还写着 A；
   *   · 本轮没产出（刷新过 / 点过清空）→ `track` 为 null → **整条不渲染**，
   *     点了完全没反应、也没有任何提示。
   *
   * 现在标题与副标题在**点击那一刻**就一起定下来，播放条不再回查产出，
   * 「点哪条响哪条」由构造保证。`nonce` 是「用户刚点了播放」的信号，
   * 见 `AudioPlayerBar.playToken` 的注释。
   */
  const [picked, setPicked] = useState<{
    url: string
    title: string
    subtitle: string
    nonce: number
  } | null>(null)

  /**
   * 模型从后端拉（**唯一真源**）。语音合成目前只有一档，但它仍然必须走这套：
   * 提交时 `JobCenterContext` 会用这个 id 去 `familyById()` 折成真实路由 slug，
   * 清单里没有它时那个 id 会被原样当 slug 发出去（详见 `config/modelFamilies.ts`
   * 里那条 audio 档的长注释）。
   */
  const { list: listModels, normalize: normalizeModel } = useModels()
  const audioModels = listModels('audio')
  const modelId = normalizeModel(form.model, 'audio')

  useEffect(() => {
    if (!audioModels.length) return
    if (modelId !== form.model) patch({ model: modelId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioModels.length, modelId])

  /**
   * 真正会发出去的音色 —— **三个地方读它**：音色抽屉的选中项、试听的参数、提交载荷。
   * 草稿里的旧 ID 在这里被回落到当前引擎的默认音色，而且回落结果**显示在界面上**
   * （用户看得见声音被换了），不是悄悄换掉。
   */
  const voiceId = pickVoiceId(form.voice, voices.groups, voices.defaultId)
  const voiceName = voices.byId(voiceId)?.name ?? voiceId

  const text = form.text.trim()
  const chars = text.length
  const longScript = chars > LONG_SCRIPT_CHARS
  /** 成片时长估算。⚠️ 与 `estimateMs` 是两件事：那个算「要等多久」，这个算「有多长」 */
  const speakSec = chars ? Math.max(1, Math.round(chars / 5)) : 0
  const canSubmit = chars > 0
  const price = usePricePreview({ appSlug: PAGE_KEY, model: modelId, outputs: 1 })

  const jobs = jobsFor(PAGE_KEY)
  const outputs = outputsFor(PAGE_KEY)

  /**
   * 试听条没被点过时**自动装上的那一条**（最新产出）。
   *
   * 两条来路的要求是相反的（新产出自己冒出来 → 不要自动出声；
   * 用户点了播放 → 要出声），所以这里给 `nonce: 0` —— 见 `AudioPlayerBar.playToken`。
   */
  const autoBar = outputs.length
    ? {
        url: outputs[0].url,
        title:
          outputs.length > 1
            ? `最新一条语音（共 ${outputs.length} 条）`
            : `语音合成 · ${text.slice(0, 14) || '本次产出'}`,
        subtitle: `${voices.engineLabel} · ${voiceName} · ${form.speed}×`,
        nonce: 0,
      }
    : null

  /**
   * 用户点过的那条**还在不在**。
   *
   * 判据要同时看两处：本轮产出，与**本页全部任务**（含从后端读回的历史记录）。
   * 只看前者的话，点历史里的一条会被判成「已消失」，条上重新装回最新一条 ——
   * 又回到「点 A 响 B」。真的不在了（产出被清理）才退回自动那一条：
   * 那时留一个指向不存在地址的播放器才是「点了没反应」的经典成因。
   */
  const pickedAlive =
    picked &&
    (outputs.some((o) => o.url === picked.url) ||
      jobs.some((j) => j.outputs.some((o) => o.url === picked.url)))
      ? picked
      : null

  const bar = pickedAlive ?? autoBar

  /**
   * 点「播放」：把**这一条**连同标题一起交给试听条，并放行出声。
   *
   * ⚠️ 标题必须在这里定：历史记录里的音频属于另一个任务，本轮产出里没有它，
   * 靠 `outputs` 派生会得到「第 1 条语音」这种假名字。所以按 URL 去 `jobs`
   * 里反查它属于哪个任务，用那个任务的真标题（就是用户看到的「语音合成 · 完整故事见上文…」）。
   */
  const pickTrack = (o: JobOutput) => {
    const job = jobs.find((j) => j.outputs.some((x) => x.url === o.url))
    const i = outputs.findIndex((x) => x.url === o.url)
    setPicked({
      url: o.url,
      title:
        i >= 0
          ? outputs.length > 1
            ? `第 ${i + 1} 条语音 · ${text.slice(0, 12) || '本次产出'}`
            : `语音合成 · ${text.slice(0, 14) || '本次产出'}`
          : (job?.title ?? '历史语音'),
      subtitle:
        i >= 0
          ? `${voices.engineLabel} · ${voiceName} · ${form.speed}×`
          : `历史产出 · ${job?.modelName ?? (job ? modelLabel(job.model) : '语音合成')}`,
      nonce: Date.now(),
    })
  }

  /**
   * 提交。
   *
   * ⚠️ `prompt` 就是**要被念出来的全文本身**，不是「给模型看的指令」。
   * 这与图片/视频页的口径相反（那边 `prompt` 是拼好的画面描述），
   * 原因是这一档的上游只有「文本」一个输入节点 —— 多塞一句
   * 「请合成下面这段」会被原样念出来，而任务照样 succeeded、钱照扣。
   *
   * ⚠️ `inputs.voice` / `inputs.speed` 是**后端 `_run_audio_phase` 直接读的键**，
   * 不经过 `_build_options`（音频在那边一个字段都不翻译）。改名要两边一起改。
   */
  const doGenerate = () =>
    flow.request({
      info: {
        title: '语音合成',
        appSlug: PAGE_KEY,
        model: modelId,
        platform: `${voices.engineLabel} · 音色 ${voiceName}`,
        outputs: 1,
        refImages: 0,
        prompt: text,
        price: price?.estimatedPrice ?? 0,
        basis: price?.basis,
      },
      args: {
        appSlug: PAGE_KEY,
        title: `语音合成 · ${text.slice(0, 16)}`,
        sourcePage: PAGE_KEY,
        model: modelId,
        prompt: text,
        inputs: {
          text,
          // 上游认的音色 ID（派生值），不是草稿里可能还留着的旧值
          voice: voiceId,
          speed: form.speed,
          // 只进用量账本，便于复盘「这条音频当初是哪个引擎出的」
          engine: voices.engine,
          __title: '语音合成',
        },
        outputs: 1,
        estimatedMs: estimateMs(chars),
      },
    })

  /* ------------------------------ 左列 · 编辑区 ------------------------------ */

  const modelName = audioModels.find((m) => m.id === modelId)?.name ?? modelId

  const composer = (
    <>
      <header className="tts-head">
        <div>
          <h1 className="tts-head__title">语音合成</h1>
          <p className="tts-head__sub">
            念出来的就是输入框里的全文 —— 别写「请朗读下面这段」这类指令，它也会被读出来
          </p>
        </div>
        {/*
          模型徽标。只有一档时**不给它画下拉箭头**（参考站那枚是带 ▾ 的）：
          点开只有一条的下拉比一个纯展示的徽标更像 bug。清单变多时自动长成可选项 ——
          判据取自接口（`audioModels.length`），不写死。
        */}
        {audioModels.length > 1 ? (
          <Segmented
            value={modelId}
            onChange={(v) => patch({ model: v })}
            options={audioModels.map((m) => ({
              value: m.id,
              label: `${m.name} ¥${m.unitPrice.toFixed(2)}/${m.unit}`,
            }))}
          />
        ) : (
          <div className="tts-head__model">
            <span className="tts-head__modelK">模型</span>
            <span className="tts-head__modelV">{modelName}</span>
          </div>
        )}
      </header>

      <div className="tts-composer">
        <textarea
          className="tts-composer__input"
          value={form.text}
          placeholder={
            '例：欢迎来到我们的直播间。今天这款硅藻土沥水垫，' +
            '洗完的杯子往上一放，水几秒钟就吸干，台面不再湿一片。'
          }
          onChange={(e) => patch({ text: e.target.value })}
          aria-label="口播稿正文"
          data-tts-input="1"
        />

        {/* 第一行：左「清空」、右「字数」。参考站这一行左边是模板/停帧、右边是字符数 */}
        <div className="tts-composer__meta">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={reset}
            disabled={!form.text}
          >
            清空
          </button>
          <span className="grow" />
          {longScript ? <span>超过 {LONG_SCRIPT_CHARS} 字会自动切段合成后再拼成一条</span> : null}
          {/*
            ⚠️ **不写「N / 10000」这种分母**。参考站有硬上限所以它写得出来，
            我们后端**没有任何长度限制**（长稿靠切段），编一个分母就是假数据。
            改成「字数 + 成片时长」—— 同样是「当前输入有多少」，而且两个数都是真的。
          */}
          <span className="tts-count" data-tts-count="1">
            <b>{chars}</b> 字
            {chars ? (
              <>
                <span className="tts-count__sep">·</span>约 {speakSec} 秒
              </>
            ) : null}
          </span>
        </div>

        {/* 第二行：左「音色 / 语速」，右「价格 / 生成」 */}
        <div className="tts-composer__bar">
          <button
            type="button"
            className="tts-chip"
            onClick={() => setVoiceOpen(true)}
            title="打开音色库（可搜索、可当场试听）"
            data-tts-voice="1"
          >
            <span className="tts-chip__k">音色</span>
            <span className="tts-chip__v">{voiceName}</span>
            <Icon name="chevron" size={14} />
          </button>

          <Segmented
            value={String(form.speed)}
            onChange={(v) => patch({ speed: Number(v) })}
            options={SPEEDS}
          />

          <span className="grow" />

          <span className="tts-cost">
            {price?.basis ?? '预估消耗'}
            <b>¥{(price?.estimatedPrice ?? 0).toFixed(2)}</b>
          </span>

          <Button
            variant="primary"
            size="lg"
            icon="sparkles"
            disabled={!canSubmit}
            onClick={doGenerate}
            title={canSubmit ? undefined : '请先写入口播稿'}
            data-tts-generate="1"
          >
            生成语音
          </Button>
        </div>
      </div>

      {/* 与参考站同一句位置的话（内容由 AI 生成 → 提示核查）。写出这条链路的真实风险 */}
      <div className="tts-note">
        内容由 AI 生成，重要信息请务必核查 —— 音色与语速由上游决定，成片请以试听为准
      </div>

      {/*
        底部试听条。**没有可播的东西时整条不渲染** ——
        空着就是一个永远点不动的播放键。
        ⚠️ 有它之后，`/tts` 页的音频就有两处可播：这里（跨产出、跨历史的**单实例**播放器）
        与产出卡里的原生播放器（就地核对）。两者不是两个入口改同一个值，见组件头注。
      */}
      {bar ? (
        <AudioPlayerBar
          track={bar}
          title={bar.title}
          subtitle={bar.subtitle}
          playToken={bar.nonce}
        />
      ) : null}

      {/*
        草稿状态那行字挪到最下面（原先在提交栏里）。它回答的是「我刚才填的东西还在吗」，
        与生成动作无关，放在按钮旁边的权重是错的。
      */}
      <div className="text-xs muted" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon name="shield" size={12} />
        {savedAt
          ? `草稿已自动保存 · ${new Date(savedAt).toLocaleTimeString('zh-CN', {
              hour: '2-digit',
              minute: '2-digit',
            })}`
          : '参数会在本机自动暂存，切页不丢'}
      </div>
    </>
  )

  /*
    这里原来有一张「这条链路」解释卡（排在右栏合并卡之后，讲口播稿直连上游的文本节点、
    音色清单出处、切段拼接那几条）。**2026-10-08 用户拍板删掉**，原话是「不要了」。

    它不是被挪走了，是这一页根本不需要：
      · 那张卡讲的三件事（念全文、清单同源、长稿拼接）都写在**左侧表单的字段提示**里，
        用户读稿子的时候就看得到，右栏再复述一遍只是把右栏往下推一屏；
      · 右栏只有一列，任何「解释卡」都是插在「本轮结果 / 本页记录」和屏幕底部之间 ——
        它占的高度是**产出位置的代价**。结果越多，这张卡越该往后站。
    别再把它加回来。真要放这类说明，放左栏表单里，别占右栏。
  */

  return (
    <div className="page page--wide">
      <GeneratorFrame
        className="tts-grid"
        form={composer}
        panel={
          <StudioPanel
            jobs={jobs}
            outputs={outputs}
            onCancel={cancel}
            onClear={clearResults}
            kind="audio"
            activeHint="语音合成是秒级的：一段 20 字的稿子约 1~3 秒，长稿按字数递增"
            resultTitle="本轮结果"
            resultEmpty="左侧写好口播稿、选好音色后点击生成，音频会出现在这里。"
            onPlayAudio={pickTrack}
          />
        }
      />

      {/*
        音色库抽屉。**它是这一页唯一的音色入口**（原来在「音色与语速」页签里）——
        「同一字段只许一个控件」：再留一个页内的完整音色库，两处都能改 `form.voice`，
        用户会以为一个是「筛选」一个是「选定」，而实际是同一个值。
      */}
      <Drawer
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        title="音色库"
        icon="wave"
        width={720}
      >
        <VoiceLibrary
          groups={voices.groups}
          value={voiceId}
          onChange={(id) => patch({ voice: id })}
          defaultId={voices.defaultId}
          engineLabel={voices.engineLabel}
          model={voices.model}
          notice={
            voices.source === 'fallback'
              ? `音色清单没读到（${voices.error || '原因不明'}），下面列的是内置兜底 —— 这**不是**全部音色。刷新页面会重新拉取。`
              : null
          }
          sourcePage={PAGE_KEY}
        />
      </Drawer>

      <PriceConfirmDialog info={flow.pending} onCancel={flow.cancel} onConfirm={flow.confirm} />
    </div>
  )
}
